import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import '../../styles/designArkAR.css';
import { launchAndroidSceneViewer, launchIosQuickLook } from '../../core/workspace/ar/nativeArLaunchers';

const MODEL_VIEWER_SRC = "https://ajax.googleapis.com/ajax/libs/model-viewer/4.3.1/model-viewer.min.js";
const DEFAULT_FALLBACK = "Interactive 3D preview is unavailable on this device.";
let modelViewerLoadPromise = null;

function ensureModelViewer() {
  if (window.customElements?.get("model-viewer")) return Promise.resolve();
  if (modelViewerLoadPromise) return modelViewerLoadPromise;
  modelViewerLoadPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (!window.customElements?.whenDefined) return reject(new Error("Custom elements unavailable"));
      window.customElements.whenDefined("model-viewer").then(resolve, reject);
    };
    const existing = document.querySelector('script[data-doar-model-viewer="1"]');
    if (existing) {
      if (window.customElements?.get("model-viewer")) resolve();
      else {
        existing.addEventListener("load", finish, { once: true });
        existing.addEventListener("error", reject, { once: true });
        finish();
      }
      return;
    }
    const script = document.createElement("script");
    script.type = "module";
    script.src = MODEL_VIEWER_SRC;
    script.dataset.doarModelViewer = "1";
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("3D viewer failed to load")), { once: true });
    document.head.appendChild(script);
  }).catch((error) => { modelViewerLoadPromise = null; throw error; });
  return modelViewerLoadPromise;
}

const fallbackStyle = { background: '#080c12', color: '#8f9baa' };
const fallbackTextStyle = { color: '#8f9baa' };

const ARProductViewer = forwardRef(function ARProductViewer(
  { product, onARAnalytics, onARUnavailable, arDisabled = false, fallbackMessage = DEFAULT_FALLBACK },
  ref
) {
  const mvRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const [viewerReady, setViewerReady] = useState(() => !!window.customElements?.get("model-viewer"));
  const [modelReady, setModelReady] = useState(false);

  useEffect(() => {
    let live = true;
    if (viewerReady) return undefined;
    ensureModelViewer().then(() => { if (live) setViewerReady(true); }).catch(() => { if (live) setHasError(true); });
    return () => { live = false; };
  }, [viewerReady]);

  useImperativeHandle(ref, () => ({
    activateAR() {
      if (arDisabled) { onARUnavailable?.(); return false; }
      onARAnalytics?.();

      // When the viewer is ready, let model-viewer launch native AR so its
      // placement semantics (floor/wall) are carried into Quick Look / Scene
      // Viewer. This is critical for window furnishings and wall-mounted work.
      const el = mvRef.current;
      if (modelReady && el && typeof el.activateAR === 'function') {
        try {
          const result = el.activateAR();
          if (result && typeof result.then === 'function') {
            result.catch(() => {
              if (launchIosQuickLook(product?.usdzUrl)) return;
              if (launchAndroidSceneViewer(product?.glbUrl, { placementProfile: product?.placementProfile })) return;
              onARUnavailable?.();
            });
          }
          return true;
        } catch {}
      }

      // Do not make the user wait for the inline preview. If it is still
      // loading, preserve the tap gesture and use the canonical native route.
      if (launchIosQuickLook(product?.usdzUrl)) return true;
      if (launchAndroidSceneViewer(product?.glbUrl, { placementProfile: product?.placementProfile })) return true;

      onARUnavailable?.();
      return false;
    },
  }), [arDisabled, modelReady, onARAnalytics, onARUnavailable, product]);

  useEffect(() => {
    if (!viewerReady) return undefined;
    const el = mvRef.current;
    if (!el) return undefined;
    const onError = () => setHasError(true);
    const onLoad = () => setModelReady(true);
    el.addEventListener('error', onError);
    el.addEventListener('load', onLoad);
    if (el.loaded) setModelReady(true);
    return () => {
      el.removeEventListener('error', onError);
      el.removeEventListener('load', onLoad);
    };
  }, [viewerReady, product?.glbUrl]);

  useEffect(() => { setHasError(false); setModelReady(false); }, [product?.glbUrl, product?.usdzUrl]);

  if (hasError) return <div className="da-mv-fallback" style={fallbackStyle}><span className="da-mv-fallback-icon" aria-hidden="true">◇</span><p style={fallbackTextStyle}>{fallbackMessage}</p></div>;
  if (!viewerReady) return <div className="da-mv-fallback" style={fallbackStyle} role="status" aria-live="polite"><span className="da-mv-fallback-icon" aria-hidden="true">◇</span><p style={fallbackTextStyle}>Loading interactive 3D preview…</p></div>;

  const arScale = product?.arScale === 'auto' ? 'auto' : 'fixed';
  const arLabel = product?.arLabel || (product?.mode === 'configurable' ? 'View at true scale' : 'Basic AR preview');
  const autoRotate = product?.autoRotate === true;

  return <div className="da-mv-wrap">
    <model-viewer
      ref={mvRef}
      src={product.glbUrl}
      {...(product.usdzUrl ? { 'ios-src': product.usdzUrl } : {})}
      {...(product.posterUrl ? { poster: product.posterUrl } : {})}
      ar=""
      // Native compatibility only - this component is a public/commerce
      // fallback surface, not the canonical DoARSpace WebXR owner (that is
      // WebXRSpatialRuntime, reached only through SpatialSession at
      // /d/:doarId/ar). Never declare "webxr" here, or model-viewer could
      // become a second first-party WebXR permission requester (contract:
      // Prompt 3B §21).
      ar-modes="scene-viewer quick-look"
      ar-placement={product.placementProfile === 'wall' || product.placementProfile === 'window_covering' ? 'wall' : 'floor'}
      ar-scale={arScale}
      camera-controls=""
      {...(autoRotate ? { 'auto-rotate': '' } : {})}
      interaction-prompt="none"
      shadow-intensity="0.7"
      exposure="1"
      reveal="auto"
      loading="eager"
      style={{ width: '100%', height: '100%', display: 'block', '--poster-color': '#e9e4db' }}
    >
      {product?.hideArButton
        ? <button slot="ar-button" type="button" hidden aria-hidden="true" tabIndex="-1" />
        : <button slot="ar-button" className="da-ar-btn da-ar-btn--slot" onClick={onARAnalytics} disabled={arDisabled || !modelReady} aria-disabled={arDisabled || !modelReady ? 'true' : undefined}><span className="da-ar-mark">AR</span>{modelReady ? arLabel : 'Preparing AR…'}</button>}
    </model-viewer>
  </div>;
});

export default ARProductViewer;
export { ensureModelViewer, launchAndroidSceneViewer, launchIosQuickLook };
